import { useState } from "react";
import {
  PencilIcon,
  TrashIcon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";
import ElectionTaskModal from "./ElectionTaskModal";
import Table from "../../common/Table";
import Modal from "../../common/Modal";
import { votersAPI } from "../../../services/api";
import { useQuery, useMutation, useQueryClient } from "react-query";

export default function ElectionPreparationModal({ isOpen, onClose }) {
  const [tasks, setTasks] = useState([]);
  const [meta, setMeta] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 5,
  });

  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [taskPendingDelete, setTaskPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const queryClient = useQueryClient();

  const tasksQuery = useQuery(
    ["election-tasks", { page: meta.current_page, per_page: meta.per_page }],
    async () => {
      const res = await votersAPI.getElectionTasks({
        page: meta.current_page,
        per_page: meta.per_page,
      });
      const responseData = res.data?.data || {};
      const list = responseData.election_preparation_tasks || [];
      const newMeta = {
        current_page: Number(responseData.meta?.current_page || 1),
        last_page: Number(responseData.meta?.last_page || 1),
        total: Number(responseData.meta?.total || 0),
        per_page: Number(responseData.meta?.per_page || meta.per_page),
      };
      setTasks(list);
      setMeta(newMeta);
      return { list, meta: newMeta };
    },
    { enabled: isOpen }
  );

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "bg-green-100 text-green-700";
      case "in progress":
        return "bg-blue-100 text-blue-700";
      case "pending":
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };


  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "bg-red-100 text-red-700";
      case "medium":
        return "bg-yellow-100 text-yellow-700";
      case "low":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const handleDeleteTask = (task) => {
    setTaskPendingDelete(task);
    setConfirmOpen(true);
  };

  const deleteTaskMutation = useMutation(
    (id) => votersAPI.deleteElectionTask(id),
    {
      onMutate: () => setDeleting(true),
      onSettled: () => setDeleting(false),
      onSuccess: () => {
        queryClient.invalidateQueries("election-tasks");
        setConfirmOpen(false);
        setTaskPendingDelete(null);
      },
    }
  );

  const confirmDeleteTask = async () => {
    if (!taskPendingDelete) return;
    await deleteTaskMutation.mutateAsync(taskPendingDelete.id);
  };

  const cancelDeleteTask = () => {
    if (deleting) return;
    setConfirmOpen(false);
    setTaskPendingDelete(null);
  };

  const columns = [
    {
      key: "title",
      label: "Task Title",
      render: (_, task) => (
        <span className="text-xs font-medium text-gray-800">{task.title}</span>
      ),
    },
    {
      key: "description",
      label: "Description",
      render: (_, task) => (
        <span className="text-xs text-gray-600">{task.description}</span>
      ),
    },
    {
      key: "due_date",
      label: "Due Date",
      render: (_, task) => (
        <span className="text-xs text-gray-600">{task.due_date || task.dueDate || "—"}</span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (_, task) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
            task.status
          )}`}
        >
          {task.status || "Pending"}
        </span>
      ),
    },
    {
      key: "priority",
      label: "Priority",
      render: (_, task) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(
            task.priority
          )}`}
        >
          {task.priority || "Low"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, task) => (
        <div className="flex gap-1">
          <button
            onClick={() => {
              setTaskToEdit(task);
              setIsTaskModalOpen(true);
            }}
            className="text-blue-600 hover:text-blue-800 rounded-full p-2 hover:bg-blue-50 transition"
          >
            <PencilIcon className="w-4 h-4" />
          </button>

          <button
            onClick={() => handleDeleteTask(task)}
            className="text-red-600 hover:text-red-800 rounded-full p-2 hover:bg-red-50 transition"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Election Preparation Tools"
        size="full"
        useHeadlessUI={false}
        alignment="top"
        panelClassName="shadow-2xl"
      >
        <hr className="border-gray-300 w-full mb-4" />

        {/* Table Header with Add Task */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <span className="p-2 bg-blue-100 text-blue-600 rounded-full">
              <ListBulletIcon className="h-4 w-4" />
            </span>
            <h2 className="text-lg font-semibold text-gray-700">
              Task Checklist
            </h2>
          </div>

          <button
            onClick={() => {
              setTaskToEdit(null);
              setIsTaskModalOpen(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition flex items-center gap-1 shadow-sm"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Task
          </button>
        </div>

        {/* Loading State */}
        {tasksQuery.isLoading ? (
          <div className="text-center py-10 text-gray-500 animate-pulse">
            Loading tasks...
          </div>
        ) : (
          <>
            {/* Scrollable Table */}
            <div className="overflow-x-auto border rounded-2xl shadow-sm">
              <div className="max-h-[28rem] overflow-y-auto">
                <Table
                  noContainer={true}
                  columns={columns}
                  data={tasks}
                  getRowKey={(task, idx) => task.id ?? task.uuid ?? `${task.title ?? "task"}-${idx}`}
                  pagination={{
                    currentPage: meta.current_page,
                    lastPage: meta.last_page,
                    perPage: meta.per_page,
                    total: meta.total,
                  }}
                  onPageChange={(page, newPerPage = meta.per_page) =>
                    setMeta((m) => ({
                      ...m,
                      current_page: page,
                      ...(newPerPage !== m.per_page
                        ? { per_page: newPerPage, current_page: 1 }
                        : null),
                    }))
                  }
                  paginationProps={{
                    onPerPageChange: (per_page) =>
                      setMeta((m) => ({ ...m, current_page: 1, per_page })),
                    showSizeSelector: true,
                    isLoading: tasksQuery.isLoading,
                  }}
                />
              </div>
            </div>

            <hr className="border-gray-300 mt-4" />
          </>
        )}
      </Modal>

      {/* Task Modal */}
      <ElectionTaskModal
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
          setTaskToEdit(null);
        }}
        task={taskToEdit}
        onSave={(taskData) => {
          if (taskToEdit) {
            setTasks(
              tasks.map((t) => (t.id === taskToEdit.id ? taskData : t))
            );
          } else {
            setTasks([taskData, ...tasks]);
          }
          // Ensure latest list is fetched
          queryClient.invalidateQueries("election-tasks");
        }}
      />

      <Modal
        isOpen={confirmOpen}
        onClose={deleting ? () => {} : cancelDeleteTask}
        title="Delete Task"
        size="sm"
        useHeadlessUI={false}
        alignment="center"
        footer={
          <div className="flex justify-end gap-2">
            <button
              onClick={cancelDeleteTask}
              disabled={deleting}
              className="px-4 py-2 rounded-lg border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={confirmDeleteTask}
              disabled={deleting}
              className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        }
      >
        <div className="text-sm text-gray-700">
          Are you sure you want to delete the task "{taskPendingDelete?.title || "Untitled"}"? This action cannot be undone.
        </div>
      </Modal>
    </>
  );
}
